import { Button, Card, Col, DatePicker, Row, Space, Descriptions, Form, Input, Select, Typography, message } from 'antd';
import {
  Entity,
  SendReportCaseV1,
  SendReportCaseV1CaseDetailsApplicationReview,
  SendReportV1CaseDetailsApplicationContainerSendReportCaseV1UpdateMutation,
  SendReportV1CaseDetailsApplicationContainerVerifiedCredentialVerificationCaseSummariesByApplicantFieldsFragment
} from '../../../../../../generated';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { FetchResult } from '@apollo/client';
import { DATE_FORMAT } from '../../../../../../constants';
import { FormatDate } from '../../../../../../constants';
import { Helmet } from 'react-helmet';
import dayjs from 'dayjs';

export interface ApplicationFormValueProps {
  caseName: string;
  destination: string;
  credential: string;
  purgeEligibleAt: Date;
  archiveEligibleAt: Date;
  expirationEligibleAt: Date;
}
interface CaseDetailsApplicationProps {
  caseId: string;
  data: SendReportCaseV1;
  onSave: (value: ApplicationFormValueProps) => Promise<FetchResult<SendReportV1CaseDetailsApplicationContainerSendReportCaseV1UpdateMutation>>;
  entities: Entity[];
  credentialCases: SendReportV1CaseDetailsApplicationContainerVerifiedCredentialVerificationCaseSummariesByApplicantFieldsFragment[];
  applicationReview: SendReportCaseV1CaseDetailsApplicationReview;
  caseState: string;
  isCurrentUserAssigned: boolean;
  purgeEligibleAt: Date;
  archiveEligibleAt: Date;
  expirationEligibleAt: Date;
  staffUserType: string;
  isSavingApplication: boolean;
  isCaseOrApplicant: string;
}

export const CaseDetailsApplication: React.FC<CaseDetailsApplicationProps> = (props) => {
  const [overrideDetailsForm] = Form.useForm<ApplicationFormValueProps>();

  const [isSavingApplication, setIsSavingApplication] = useState(props.isSavingApplication);

  useEffect(() => {
    setIsSavingApplication(props.isSavingApplication);
    dispatch({ type: 'save-date-details' });
  }, [props.isSavingApplication]);

  const labelStyle = {
    fontSize: '12px'
  };

  const labelTitleStyle = {
    fontSize: '15px',
    fontWeight: 400
  };

  interface ApplicationFormState {
    isSendDestinationEditable: boolean;
    isCaseNameEditable: boolean;
    isCredentialEditable: boolean;
    isDatesEditable: boolean;
  }

  const initialStage: ApplicationFormState = {
    isSendDestinationEditable: false,
    isCaseNameEditable: false,
    isCredentialEditable: false,
    isDatesEditable: false
  };

  const ApplicationFormAction = {
    EditCaseName: 'edit-case-name',
    SaveCaseName: 'save-case-name',
    CancelEditCaseName: 'cancel-edit-case-name',
    EditCredential: 'edit-credential',
    SaveCredential: 'save-credential',
    CancelEditCredential: 'cancel-edit-credential',
    EditDestination: 'edit-destination',
    SaveDestination: 'save-destination',
    CancelEditDestination: 'cancel-edit-destination',
    EditDates: 'edit-dates',
    SaveDates: 'save-dates',
    CancelEditDate: 'cancel-edit-dates'
  };
  const disabled =
    !props.isCurrentUserAssigned ||
    props.caseState === 'draft' ||
    props.caseState === 'decisionRendered' ||
    (props.caseState === 'revisionRequested' && props.isCurrentUserAssigned);

  const reducer = (state: ApplicationFormState, action: { type: string }) => {
    switch (action.type) {
      case ApplicationFormAction.EditCaseName:
        return {
          ...state,
          isCaseNameEditable: true
        };
      case ApplicationFormAction.CancelEditCaseName:
        setIsSavingApplication(false);
        return {
          ...state,
          isCaseNameEditable: false
        };
      case ApplicationFormAction.SaveCaseName:
        return {
          ...state,
          isCaseNameEditable: false
        };
      case ApplicationFormAction.EditCredential:
        return {
          ...state,
          isCredentialEditable: true
        };
      case ApplicationFormAction.CancelEditCredential:
        setIsSavingApplication(false);
        return {
          ...state,
          isCredentialEditable: false
        };
      case ApplicationFormAction.SaveCredential:
        return {
          ...state,
          isCredentialEditable: false
        };
      case ApplicationFormAction.EditDestination:
        return {
          ...state,
          isSendDestinationEditable: true
        };
      case ApplicationFormAction.CancelEditDestination:
        setIsSavingApplication(false);
        return {
          ...state,
          isSendDestinationEditable: false
        };
      case ApplicationFormAction.SaveDestination:
        return {
          ...state,
          isSendDestinationEditable: false
        };
      case ApplicationFormAction.EditDates:
        return {
          ...state,
          isDatesEditable: true
        };
      case ApplicationFormAction.CancelEditDate:
        setIsSavingApplication(false);
        return {
          ...state,
          isDatesEditable: false
        };
      case ApplicationFormAction.SaveDates:
        return {
          ...state,
          isDatesEditable: false
        };
      default:
        return state;
    }
  };

  const handleSaveData = async (values: ApplicationFormValueProps, disPatchApplicationFormAction: string) => {
    try {
      const response = await props.onSave(values);
      if (response.data?.sendReportCaseV1Update.status.success) {
        message.success('Application data saved successfully');
        dispatch({ type: disPatchApplicationFormAction });
      } else {
        message.error("Error: Application data can't be saved in this case state");
      }
    } catch (error) {
      message.error('Error saving Application data');
    }
  };

  const handleSaveCaseName = () => {
    caseNameForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveCaseName);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const handleSaveCredential = () => {
    credentialForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveCredential);
      })
      .catch((errorInfo) => console.log('Form Validation failed:', errorInfo));
  };

  const handleSaveDestination = () => {
    sendDestinationForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveDestination);
      })
      .catch((errorInfo) => console.log('Form Validation failed:', errorInfo));
  };
  const handleSaveOverrideDetails = async (values: ApplicationFormValueProps) => {
    props.onSave(values).then(() => {
      message.success('Application data saved successfully');
      dispatch({ type: 'cancel-edit-dates' });
    });
  };

  const clientEntities = useMemo(() => props.entities.filter((entity) => entity.isClient), [props.entities]);
  const [caseNameForm] = Form.useForm<ApplicationFormValueProps>();
  const [credentialForm] = Form.useForm<ApplicationFormValueProps>();
  const [sendDestinationForm] = Form.useForm<ApplicationFormValueProps>();

  const handleCancelEditCaseName = () => {
    dispatch({
      type: 'cancel-edit-case-name'
    });
    caseNameForm.resetFields();
  };

  const handleCancelEditCredentials = () => {
    dispatch({
      type: 'cancel-edit-credential'
    });
    credentialForm.resetFields();
  };

  const handleCancelEditDestination = () => {
    dispatch({
      type: 'cancel-edit-destination'
    });
    sendDestinationForm.resetFields();
  };

  const [applicationFormState, dispatch] = useReducer(reducer, initialStage);
  return (
    <>
      <Helmet>
        <title>{props?.isCaseOrApplicant} - Application</title>
      </Helmet>
      <Card hoverable style={{ marginTop: '20px', padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingLeft: 30,
              width: '100%'
            }}
          >
            {!applicationFormState.isCaseNameEditable && (
              <Descriptions
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() => {
                      dispatch({
                        type: 'edit-case-name'
                      });
                    }}
                  >
                    Edit
                  </Button>
                }
                contentStyle={{ paddingLeft: 25 }}
                layout="vertical"
                title={<Typography.Title style={{ ...labelTitleStyle }}>Case Name</Typography.Title>}
              >
                <Descriptions.Item style={{ paddingBottom: '8px' }}>{props.data.caseName || 'N/A'}</Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isCaseNameEditable && (
              <Form
                form={caseNameForm}
                initialValues={{
                  caseName: props.data.caseName
                }}
                layout="vertical"
                onFinish={handleSaveCaseName}
              >
                <Row>
                  <Col flex="auto">
                    <Form.Item
                      name={'caseName'}
                      label="Case Name"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter case name.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter case name" style={{ width: 200 }} disabled={disabled} />
                    </Form.Item>
                  </Col>
                  <Col flex={'100px'} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleCancelEditCaseName}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                        Save
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
            <Typography.Text style={{ fontSize: '20px', fontWeight: 600, marginTop: '24px' }}>Applicant Provided Details</Typography.Text>
            <Typography.Text style={{ ...labelStyle, marginBottom: '24px' }}>(Visible to Applicant)</Typography.Text>
            {!applicationFormState.isCredentialEditable && (
              <Descriptions
                title="Which Credential do you want to send?"
                labelStyle={labelStyle}
                layout="vertical"
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() => {
                      dispatch({
                        type: 'edit-credential'
                      });
                    }}
                  >
                    Edit
                    {/* edit-credential */}
                  </Button>
                }
              >
                <Descriptions.Item>
                  <Typography.Text strong style={{ marginTop: '12px' }}>{`${
                    props.data.caseDetails?.application?.credential?.caseDetails?.application?.issuingInstitution?.entityName ??
                    props.data.caseDetails?.application?.credential?.caseDetails?.application?.otherIssuingInstitution?.institutionName
                  } - ${props.data.caseDetails?.application?.credential?.caseDetails?.application?.credentialTitle}`}</Typography.Text>
                </Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isCredentialEditable && (
              <Form
                form={credentialForm}
                initialValues={{
                  credential: props.data.caseDetails?.application?.credential?.id
                }}
                layout="vertical"
                onFinish={handleSaveCredential}
              >
                <Row>
                  <Col flex="auto">
                    <Form.Item
                      name={['credential']}
                      label="Which Credential do you want to send?"
                      rules={[
                        {
                          required: true,
                          message: 'Please select a credential.'
                        }
                      ]}
                    >
                      <Select placeholder="Select Credential" style={{ width: 300 }} disabled={disabled}>
                        {props.credentialCases
                          .map((credential) => (
                            <Select.Option key={credential.id} value={credential.id}>
                              {`${
                                credential.caseDetails?.application?.issuingInstitutionName
                              } - ${credential.caseDetails?.application?.credentialTitle}`}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col flex={'100px'} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleCancelEditCredentials}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                        Save
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
            {!applicationFormState.isSendDestinationEditable && (
              <Descriptions
                title="Where do you want to send this Credential?"
                labelStyle={labelStyle}
                layout="vertical"
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() => dispatch({ type: 'edit-destination' })}
                  >
                    Edit
                  </Button>
                }
                style={{ marginTop: '24px' }}
              >
                <Descriptions.Item label="Institution Name">{props.data.caseDetails?.application?.destination?.entityName ?? 'N/A'}</Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isSendDestinationEditable && (
              <Form
                layout="vertical"
                form={sendDestinationForm}
                initialValues={{
                  destination: props.data.caseDetails?.application?.destination?.id
                }}
                onFinish={handleSaveDestination}
              >
                <Row>
                  <Descriptions
                    title="Where do you want to send this Credential?"
                    labelStyle={labelStyle}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right' }}>
                        <Space>
                          <Button onClick={handleCancelEditDestination}>Cancel</Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    style={{ marginTop: '24px' }}
                  />
                  <Col flex="auto">
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: 'Please select a Destination.'
                        }
                      ]}
                      name={['destination']}
                      style={{ marginTop: '24px' }}
                    >
                      <Select placeholder="Select Destination" style={{ width: 300 }} disabled={disabled}>
                        {clientEntities.map((entity) => (
                          <Select.Option key={entity.id} value={entity.id}>
                            {entity.entityName}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
            <Descriptions layout="vertical" style={{ marginTop: '40px' }} />
            <Typography.Text style={{ fontSize: '20px', fontWeight: '600' }}>Private Case Details</Typography.Text>
            <Typography.Text style={{ fontSize: '12px' }}>(Not Visible to Applicant)</Typography.Text>
            <br />
            <Descriptions
              layout="vertical"
              labelStyle={labelStyle}
              title={<Typography.Text style={{ fontSize: '16px', fontWeight: '400' }}>Institution Contact Details</Typography.Text>}
            >
              <Descriptions.Item label="Institution Email" labelStyle={labelStyle}>
                {props.applicationReview.privateCaseDetails?.institutionContactDetails?.institutionEmail ?? 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Institution Phone" labelStyle={labelStyle}>
                {props.applicationReview.privateCaseDetails?.institutionContactDetails?.institutionPhone ?? 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            {!applicationFormState.isDatesEditable && props?.staffUserType === 'Staff.IS' && (
              <>
                <br />
                <Descriptions
                  title={<Typography.Text style={{ fontSize: '16px', fontWeight: '400' }}>Dates Details</Typography.Text>}
                  layout="vertical"
                  labelStyle={labelStyle}
                  extra={
                    <Button
                      type="primary"
                      disabled={Object.values(applicationFormState).some((value) => value)}
                      onClick={() => {
                        dispatch({
                          type: 'edit-dates'
                        });
                      }}
                    >
                      Edit
                    </Button>
                  }
                >
                  <Descriptions.Item label="Purge Eligible At">
                    {props.purgeEligibleAt ? FormatDate(props.purgeEligibleAt ): 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Archive Eligible At">
                    {props.archiveEligibleAt ? FormatDate(props.archiveEligibleAt) : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Expiration Eligible At">
                    {props.expirationEligibleAt ? FormatDate(props.expirationEligibleAt) : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {applicationFormState.isDatesEditable && (
              <Form
                form={overrideDetailsForm}
                style={{ width: '100%' }}
                layout="vertical"
                initialValues={{
                  purgeEligibleAt: props.purgeEligibleAt ? dayjs(props.purgeEligibleAt) : null,
                  archiveEligibleAt: props.archiveEligibleAt ? dayjs(props.archiveEligibleAt) : null,
                  expirationEligibleAt: props.expirationEligibleAt ? dayjs(props.expirationEligibleAt) : null
                }}
                onFinish={handleSaveOverrideDetails}
              >
                <Row>
                  <Descriptions
                    column={2}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right' }}>
                        <Space>
                          <Button
                            onClick={() => {
                              dispatch({
                                type: 'cancel-edit-dates'
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    title={<Typography.Text style={{ ...labelTitleStyle }}>Dates Details</Typography.Text>}
                    labelStyle={labelStyle}
                    style={{ marginTop: '24px' }}
                  />
                  <Col flex="auto">
                    <Form.Item label="Purge Eligible At" name={['purgeEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={props.purgeEligibleAt}
                        placeholder="Purge Eligible At"
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                        disabled={props.caseState !== 'draft'}
                      />
                    </Form.Item>
                    <Form.Item label="Archive Eligible At" name={['archiveEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={props.archiveEligibleAt}
                        placeholder="Archive Eligible At"
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                      />
                    </Form.Item>
                    <Form.Item label="Expiration Eligible At" name={['expirationEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={props.expirationEligibleAt}
                        placeholder="Expiration Eligible At"
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
          </div>
        </div>
      </Card>
    </>
  );
};
